import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer, inject } from 'mobx-react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

class Geolocation extends Component {
  state = {
    subscribed: false,
    position: null,
    toast: false,
  };
  intervalRef = null;

  componentDidMount() {
    this.setupGeolocation();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.active !== this.props.active) {
      this.setupGeolocation();
    }
  }

  componentWillUnmount() {
    this.stopPeriodicLogging();
  }

  fetchPosition = () => new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation not available'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => reject(err),
      { enableHighAccuracy: true }
    );
  });

  logPosition = async (pos) => {
    const payload = {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      timestamp: pos.timestamp || Date.now(),
    };

    try {
      const resp = await fetch(this.props.appState.appRoutine.apiUrl + 'geo/insert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: payload, serviceId: this.props.serviceId }),
      });
      if (!resp.ok) console.warn('Failed to send geo to server', resp.status);
      else { 
        console.log('Geo saved on server'); 
        this.setState({ toast: true }); 
        setTimeout(() => this.setState({ toast: false }), 6000); 
      }
    } catch (err) {
      console.warn('Error sending geo to server');
    }
  };

  startPeriodicLogging = () => {
    this.setState({ subscribed: true });
    this.fetchPosition()
      .then((pos) => {
        this.setState({ position: pos });
        this.logPosition(pos);
      })
      .catch(() => {});

    this.intervalRef = setInterval(() => {
      this.fetchPosition()
        .then((pos) => {
          this.setState({ position: pos });
          this.logPosition(pos);
        })
        .catch(() => {});
    }, 30000);
  };

  stopPeriodicLogging = () => {
    this.setState({ subscribed: false });
    if (this.intervalRef !== null) {
      clearInterval(this.intervalRef);
      this.intervalRef = null;
    }
  };

  setupGeolocation = () => {
    if (this.props.active && typeof navigator !== 'undefined' && navigator.geolocation) {
      this.startPeriodicLogging();
    } else {
      this.stopPeriodicLogging();
    }
  };

  render() {
    const { toast } = this.state;
    if (!toast) return null;
    return (
      <div style={{
        position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
        background: '#2e7d32', color: '#fff', padding: '10px 22px', borderRadius: 6,
        fontSize: 14, fontWeight: 500, zIndex: 99999, boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
        pointerEvents: 'none',
      }}>Location shared</div>
    );
  }
}

Geolocation.propTypes = {
  active: PropTypes.bool,
  appState: PropTypes.object,
  serviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Geolocation.defaultProps = {
  active: false,
};

// LiveLocationDialog: fetch latest geo and show map
class LiveLocationDialogBase extends Component {
  state = { locations: [] };
  refreshIntervalRef = null;
  map = null;
  markers = [];
  mapContainerRef = React.createRef();

  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.open && this.props.open) {
      this.fetchLatest();
      this.startAutoRefresh();
    }
    if (prevProps.open && !this.props.open) {
      this.stopAutoRefresh();
      this.destroyMap();
    }
    if (this.state.locations !== prevState.locations && this.state.locations.length > 0) {
      if (!this.map && this.props.open) {
        const latest = this.state.locations[this.state.locations.length - 1];
        this.initMap(latest.latitude, latest.longitude);
      } else if (this.map) {
        this.updateMarkers();
      }
    }
  }

  componentWillUnmount() {
    this.stopAutoRefresh();
    this.destroyMap();
  }

  startAutoRefresh = () => {
    this.stopAutoRefresh();
    this.refreshIntervalRef = setInterval(() => this.fetchLatest(), 30000);
  };

  stopAutoRefresh = () => {
    if (this.refreshIntervalRef) {
      clearInterval(this.refreshIntervalRef);
      this.refreshIntervalRef = null;
    }
  };

  fetchLatest = () => {
    const { serviceId } = this.props;
    if (!serviceId) { this.setState({ locations: [] }); return; }
    fetch(this.props.appState.appRoutine.apiUrl + 'geo/view?serviceId=' + encodeURIComponent(serviceId), { method: 'GET', headers: { 'Content-Type': 'application/json' } })
      .then(r => r.json())
      .then(data => {
        if (data && data.LOCATION) {
          const raw = data.LOCATION;
          const locs = Array.isArray(raw) ? raw : [raw];
          const valid = locs.filter(l => (l.latitude || l.lat) && (l.longitude || l.lon || l.lng));
          this.setState({ locations: valid });
        } else {
          this.setState({ locations: [] });
        }
      })
      .catch(() => this.setState({ locations: [] }));
  };

  ensurePulsingCss = () => {
    if (document.getElementById('live-location-pulse-css')) return;
    const css = `
      .lt-map-wrap { position: relative; width: 100%; height: 100%; overflow: hidden; }
      .lt-map-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
      /* Leaflet div-icon marker styling */
      .lt-leaflet-dot-icon { display: inline-block; width: 30px; height: 30px; }
      .lt-leaflet-dot { position: relative; width: 14px; height: 14px; border-radius: 50%; background: rgba(0,123,255,1); margin: 8px; }
      .lt-leaflet-dot::after { content: ''; position: absolute; left: -10px; top: -12px; width: 36px; height: 36px; border-radius: 50%; background: rgba(0,123,255,0.35); animation: lt-pulse 1.6s ease-out infinite; }
      .lt-leaflet-stale-dot { position: relative; width: 14px; height: 14px; border-radius: 50%; background: rgba(139,69,255,0.9); margin: 8px; }
      .lt-leaflet-dot-stale { position: relative; width: 14px; height: 14px; border-radius: 50%; background: rgba(0,123,255,0.9); margin: 8px; }
      .lt-timestamp { position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); background: rgba(255,255,255,0.98); padding: 8px 14px; border-radius: 6px; font-size: 13px; color: #333; z-index: 10000; box-shadow: 0 4px 14px rgba(0,0,0,0.18); white-space: nowrap; pointer-events: none; font-weight: 500; }
      @keyframes lt-pulse { 0% { transform: scale(0.6); opacity: 0.9 } 70% { transform: scale(1.4); opacity: 0 } 100% { transform: scale(1.4); opacity: 0 } }
    `;
    const style = document.createElement('style');
    style.id = 'live-location-pulse-css';
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  };

  initMap = (lat, lon) => {
    if (!this.mapContainerRef.current) return;
    try {
      this.map = L.map(this.mapContainerRef.current, { center: [lat, lon], zoom: 16 });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors' }).addTo(this.map);
      try { if (this.map.attributionControl) this.map.attributionControl.setPrefix(''); } catch (e) {}
      this.updateMarkers();
    } catch (e) {
      console.warn('Leaflet init failed', e);
      this.destroyMap();
    }
  };

  clusterLocations = (locations) => {
    // ~10m threshold in degrees
    const THRESHOLD = 0.0001;
    const clusters = [];
    locations.forEach((loc, i) => {
      const lat = Number(loc.latitude || loc.lat);
      const lon = Number(loc.longitude || loc.lon || loc.lng);
      const existing = clusters.find(c =>
        Math.abs(c.lat - lat) < THRESHOLD && Math.abs(c.lon - lon) < THRESHOLD
      );
      if (existing) {
        existing.entries.push({ loc, i });
      } else {
        clusters.push({ lat, lon, entries: [{ loc, i }] });
      }
    });
    return clusters;
  };

  updateMarkers = () => {
    if (!this.map) return;
    this.markers.forEach(m => m.remove());
    this.markers = [];
    const { locations } = this.state;
    if (!locations.length) return;
    const lastIndex = locations.length - 1;
    const clusters = this.clusterLocations(locations);
    clusters.forEach(({ lat, lon, entries }) => {
      const hasLatest = entries.some(e => e.i === lastIndex);
      const isLive = hasLatest && this.isLiveLocation(locations[lastIndex].timestamp);
      const icon = this.createIcon(hasLatest, isLive);
      const times = entries.map(e => `<div>${this.formatTime(e.loc.timestamp)}</div>`).join('');
      const m = L.marker([lat, lon], { icon }).addTo(this.map);
      m.bindPopup(`<div style="font-size:13px;font-weight:500;line-height:1.8">${times}</div>`);
      this.markers.push(m);
    });
  };

  createIcon = (isLatest, isLive) => {
    const cls = (isLatest && isLive) ? 'lt-leaflet-dot' : 'lt-leaflet-stale-dot';
    const html = `<div class="lt-leaflet-dot-icon"><div class="${cls}"></div></div>`;
    return L.divIcon({ className: '', html, iconSize: [30, 30], iconAnchor: [15, 15] });
  };

  destroyMap = () => {
    if (this.map) {
      try { this.map.remove(); } catch (e) {}
      this.map = null;
      this.markers = [];
    }
  };

  isLiveLocation = (timestamp) => {
    if (!timestamp) return false;
    const now = Date.now();
    const diffMins = (now - timestamp) / 60000;
    return diffMins < 3;
  };

  formatTime = (timestamp) => {
    if (!timestamp) return '';
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) +
      ', ' + d.toLocaleDateString([], { day: '2-digit', month: 'short' });
  };

  render() {
    const { locations } = this.state;
    const { open, onClose } = this.props;
    if (locations.length > 0) this.ensurePulsingCss();

    return (
      <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Live Location</DialogTitle>
        <DialogContent style={{ height: 400, padding: locations.length ? 0 : 16, display: locations.length ? 'block' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {locations.length > 0
            ? <div ref={this.mapContainerRef} style={{ width: '100%', height: '100%' }} />
            : <div style={{ color: '#999', fontSize: 16, textTransform: 'uppercase', fontWeight: 300, letterSpacing: 1 }}>No Location Available</div>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export const LiveLocationDialog = inject('appState')(observer(LiveLocationDialogBase));

export default inject('appState')(observer(Geolocation));
